import { CheckCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { getUserProfile, getUserMessage } from "./services/api";
import { useToast } from "./components/ToastContext";

const CARDS = {
  free_trial: {
    name: "Free Trial",
    tagline: "Try it out, no strings attached",
    price: "FREE",
    priceNote: "One-time · 14 days",
    mascot: "/assets/mascot_casual.png",
    cta: "Start Free Trial",
    limits: ["15 resume tailors", "Up to 3 per batch", "No cover letters"],
    features: [
      "AI resume tailoring",
      "ATS match scoring",
      "Job saving & tracking",
      "Application history",
      "Skill gap analysis",
    ],
    featured: false,
  },
  starter: {
    name: "Starter",
    tagline: "For your active job search",
    price: "$9.99",
    priceNote: "per month",
    mascot: "/assets/mascot_starter.png",
    cta: "Get Starter",
    limits: ["30 resume tailors", "Up to 10 per batch", "15 cover letters"],
    features: [
      "Everything in Free Trial, plus",
      "Cover letter generation",
      "Larger batch tailoring",
      "Full application insights",
    ],
    featured: false,
  },
  pro: {
    name: "Pro",
    tagline: "Maximum power for serious applicants",
    price: "$19.99",
    priceNote: "per month",
    mascot: "/assets/mascot_pro.png",
    cta: "Go Pro",
    limits: ["60 resume tailors", "Up to 15 per batch", "60 cover letters"],
    features: [
      "Everything in Starter, plus",
      "2× tailoring allowance",
      "4× cover letter allowance",
      "Largest batch operations",
    ],
    featured: true,
  },
};

const tiers = ["free_trial", "starter", "pro"];

const PricingCard = ({
  tier,
  card,
  selected,
  onSelect,
  alreadyUsed,
  isCurrent,
}) => {
  const { featured } = card;
  const isSelected = selected === tier;

  return (
    <div
      onClick={() => !alreadyUsed && onSelect(tier)}
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderRadius: "20px",
        padding: featured ? "2rem" : "1.75rem",
        background: !isSelected ? "#fff" : "#f7f4ffdc",
        boxShadow: isSelected
          ? "5px 8px 0 3px #402c7a, 10px 40px 20px 25px rgba(79,70,229,0.18)"
          : featured
            ? "0 20px 48px rgba(79,70,229,0.14)"
            : "0 4px 16px rgba(79,70,229,0.06)",
        transform: featured ? "scale(1.04)" : "scale(1)",
        cursor: alreadyUsed ? "not-allowed" : "pointer",
        opacity: alreadyUsed ? 0.55 : 1,
        transition: "box-shadow 0.18s, transform 0.18s",
        position: "relative",
        zIndex: featured ? 1 : 0,
      }}
      onMouseEnter={(e) => {
        if (!alreadyUsed && !isSelected)
          e.currentTarget.style.boxShadow = featured
            ? "0 24px 56px rgba(79,70,229,0.2)"
            : "0 8px 24px rgba(79,70,229,0.12)";
      }}
      onMouseLeave={(e) => {
        if (!isSelected)
          e.currentTarget.style.boxShadow = isSelected
            ? "0 0 0 3px #402c7a, 0 20px 48px rgba(79,70,229,0.18)"
            : featured
              ? "0 20px 48px rgba(79,70,229,0.14)"
              : "0 4px 16px rgba(79,70,229,0.06)";
      }}
    >
      {featured && (
        <div
          style={{
            position: "absolute",
            top: "-14px",
            left: "50%",
            transform: "translateX(-50%)",
            background: "linear-gradient(135deg, #402c7a, #826dc0)",
            color: "#fff",
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            padding: "0.3rem 1rem",
            borderRadius: "999px",
            whiteSpace: "nowrap",
          }}
        >
          MOST POPULAR
        </div>
      )}

      {/* Mascot */}
      <div style={{ textAlign: "center", marginBottom: "1rem" }}>
        <img
          src={card.mascot}
          alt={card.name}
          style={{
            width: "88px",
            height: "88px",
            objectFit: "contain",
            margin: "0 auto",
          }}
        />
      </div>

      {/* Name & tagline */}
      <div style={{ marginBottom: "1rem" }}>
        <h2
          style={{
            fontSize: "1.75rem",
            fontWeight: 800,
            color: "#402c7a",
            lineHeight: 1.1,
            marginBottom: "0.3rem",
          }}
        >
          {card.name}
        </h2>
        <p style={{ fontSize: "0.85rem", color: "#6b7280" }}>{card.tagline}</p>
      </div>

      {/* Price */}
      <div style={{ marginBottom: "1.25rem" }}>
        <span
          style={{
            fontSize: "2.25rem",
            fontWeight: 800,
            color: "#402c7a",
            lineHeight: 1,
          }}
        >
          {card.price}
        </span>
        <span
          style={{ fontSize: "0.8rem", color: "#9ca3af", marginLeft: "0.4rem" }}
        >
          {card.priceNote}
        </span>
      </div>

      {/* CTA */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (!alreadyUsed) onSelect(tier);
        }}
        disabled={alreadyUsed}
        style={{
          width: "100%",
          padding: "0.7rem 1.25rem",
          borderRadius: "999px",
          border: "none",
          background: isSelected
            ? "#eeebfb"
            : "linear-gradient(135deg, #402c7a, #826dc0)",
          color: isSelected ? "#402c7a" : "#fff",
          fontWeight: 700,
          fontSize: "0.9rem",
          cursor: alreadyUsed ? "not-allowed" : "pointer",
          marginBottom: "1.5rem",
          transition: "opacity 0.15s",
        }}
      >
        {isCurrent
          ? "Current Plan"
          : alreadyUsed
            ? "Already Used"
            : isSelected
              ? "✓ Selected"
              : card.cta}
      </button>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <p
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#402c7a",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: "0.6rem",
            }}
          >
            Resume Limits
          </p>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: "0 0 1.25rem 0",
              display: "flex",
              flexDirection: "column",
              gap: "0.45rem",
            }}
          >
            {card.limits.map((l, i) => (
              <li
                key={i}
                style={{
                  fontSize: "0.875rem",
                  color: "#374151",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                }}
              >
                <span
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    background: "#402c7a",
                    flexShrink: 0,
                  }}
                />
                {l}
              </li>
            ))}
          </ul>
        </div>
        <div
          style={{
            borderLeft: "1px solid",
            borderColor: featured ? "#e5e7eb" : "#8b7fb1",
            paddingLeft: "1.25rem",
          }}
        >
          <p
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              color: "#402c7a",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
              marginBottom: "0.6rem",
            }}
          >
            Key Features
          </p>
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.45rem",
            }}
          >
            {card.features.map((f, i) => (
              <li
                key={i}
                style={{
                  fontSize: "0.875rem",
                  color: "#374151",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.5rem",
                }}
              >
                <CheckCircle
                  size={14}
                  style={{ color: "#402c7a", flexShrink: 0, marginTop: "2px" }}
                />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const SubscriptionPlansScreen = ({
  userEmail,
  subscriptionCounts,
  setSubscriptionCounts,
  handleCreateCheckoutSession,
  handleSaveSubscription,
  handleCancelSubscription,
  navigateBack,
  handleLogout,
  navigateTo,
  handleErrors,
}) => {
  const toast = useToast();
  const [selectedTier, setSelectedTier] = useState(null);

  useEffect(() => {
    setSelectedTier(
      subscriptionCounts.tier === null ? "free_trial" : subscriptionCounts.tier,
    );
  }, []);

  const cancelMembership = async () => {
    const confirmCancel = window.confirm(
      "Are you sure you want to cancel your subscription? You will retain access to your current tier's benefits until the end of your billing cycle.",
    );
    if (!confirmCancel) return;

    try {
      await handleCancelSubscription();
      toast.success(
        "Subscription cancelled. You'll keep access until the end of your billing cycle. Redirecting to login.",
      );
      const userInfo = await getUserProfile(userEmail, true, true, false, true);
      if (userInfo.subscription) {
        const {
          resumes_used,
          resumes_limit,
          cover_letters_generated,
          cover_letters_limit,
          end_date,
          free_trial_used,
          batch_limit,
          billing_cycle_end,
          tier,
        } = userInfo.subscription;
        setSubscriptionCounts({
          resumes_used,
          resumes_limit,
          cover_letters_generated,
          cover_letters_limit,
          end_date,
          free_trial_used,
          batch_limit,
          billing_cycle_end,
          tier,
        });
      }
      handleLogout();
    } catch (error) {
      handleErrors("Cancel subscription", error);
      toast.error(getUserMessage(error));
    }
  };

  const handleChoosePlan = async () => {
    try {
      if (selectedTier !== subscriptionCounts.tier) {
        if (selectedTier !== "free_trial") {
          const { url } = await handleCreateCheckoutSession(selectedTier);
          window.location.href = url;
        } else {
          await handleSaveSubscription({});
        }
        const userInfo = await getUserProfile(
          userEmail,
          true,
          true,
          false,
          true,
        );
        if (userInfo.subscription) {
          const {
            resumes_used,
            resumes_limit,
            cover_letters_generated,
            cover_letters_limit,
            end_date,
            free_trial_used,
            batch_limit,
            billing_cycle_end,
            tier,
          } = userInfo.subscription;
          setSubscriptionCounts({
            resumes_used,
            resumes_limit,
            cover_letters_generated,
            cover_letters_limit,
            end_date,
            free_trial_used,
            batch_limit,
            billing_cycle_end,
            tier,
          });
        }
      }
      navigateTo("home");
    } catch (error) {
      handleErrors("Stripe checkout", error);
      toast.error(getUserMessage(error));
    }
  };

  return (
    <div className="min-h-screen bg-loading-color">
      <div
        style={{
          maxWidth: "56rem",
          margin: "0 auto",
          padding: "2.5rem 1.5rem 3rem",
        }}
      >
        {/* Current subscription banner */}
        {subscriptionCounts?.tier && subscriptionCounts.tier !== "expired" && (
          <div
            style={{
              background: "#eeebfb",
              border: "1px solid rgb(64, 44, 122)",
              borderRadius: "12px",
              padding: "1rem 1.25rem",
              marginBottom: "2rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: "0.75rem",
            }}
          >
            <div>
              <p
                style={{
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  color: "#402c7a",
                  marginBottom: "0.2rem",
                }}
              >
                Current plan:{" "}
                {subscriptionCounts.tier
                  .replace("_", " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase())}
              </p>
              <p style={{ fontSize: "0.8rem", color: "#6b7280" }}>
                Renews{" "}
                {new Date(subscriptionCounts.end_date).toLocaleDateString()}
              </p>
            </div>
            <button onClick={cancelMembership} className="buttons-red">
              Cancel plan
            </button>
          </div>
        )}

        {subscriptionCounts?.tier === "expired" && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "12px",
              padding: "1rem 1.25rem",
              marginBottom: "2rem",
            }}
          >
            <p
              style={{
                fontSize: "0.875rem",
                fontWeight: 600,
                color: "#dc2626",
              }}
            >
              Your subscription has expired. Choose a plan below to continue.
            </p>
          </div>
        )}

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: "1rem" }}>
          <h1 className="applicant-theme-text">Choose your plan</h1>
          <p style={{ color: "#6b7280", fontSize: "1rem" }}>
            Start free. Upgrade when you're ready.
          </p>
        </div>

        {/* Cards */}
        <div
          style={{
            gap: "1.25rem",
            alignItems: "stretch",
            paddingTop: "1rem",
          }}
        >
          {tiers.map((tier) => (
            <PricingCard
              key={tier}
              tier={tier}
              card={CARDS[tier]}
              selected={selectedTier}
              onSelect={setSelectedTier}
              alreadyUsed={
                !subscriptionCounts.tier === "free_trial" &&
                subscriptionCounts.free_trial_used
              }
              isCurrent={tier === subscriptionCounts.tier}
            />
          ))}
        </div>

        {/* Continue button */}
        <div
          style={{
            marginTop: "2.5rem",
            flexDirection: "column",
            alignItems: "center",
            gap: "0.75rem",
          }}
        >
          <div className="buttons-container">
            <button
              onClick={handleChoosePlan}
              disabled={
                !selectedTier ||
                (!subscriptionCounts?.tier === "free_trial" &&
                  subscriptionCounts.free_trial_used)
              }
              className="buttons-indigo"
            >
              Continue
            </button>
          </div>

          <p style={{ fontSize: "0.75rem", color: "#9ca3af" }}>
            Cancel anytime · No hidden fees ·{" "}
            {selectedTier === "free_trial"
              ? "No credit card required"
              : "Secure checkout via Stripe"}
          </p>
          <p style={{ fontSize: "0.72rem", color: "#9ca3af" }}>
            Free trials are available for new customers only. Existing customers
            can earn a second trial via testimonial submission.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlansScreen;
